import { agentControllerRefSchema } from "../daemon/agent-controller-ref.js";
import { agentProfileCreationPreferenceSelectionSchema } from "./agent-profile-binding.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/agent-profile-creation-request.ts
const identifierSchema = z$1.string().trim().min(1).max(256);
const TEAMMATE_AGENT_REQUESTS_ENABLED = false;
const profileNameSchema = z$1.string().trim().min(1).max(80);
const isoDateTimeSchema = z$1.iso.datetime({ offset: true });
const agentProfileCreationRequestSelectorSchema = z$1.object({
	requestId: identifierSchema.optional(),
	idempotencyKey: identifierSchema.optional()
}).refine((value) => !(value.requestId && value.idempotencyKey), { message: "Select a request by its ID or idempotency key, not both." });
const agentProfileCreationExecutionMessageSchema = z$1.strictObject({
	kind: z$1.literal("agent_profile_creation"),
	v: z$1.literal(1),
	organizationId: identifierSchema,
	requestId: identifierSchema
});
const agentProfileCreationApprovalPolicySchema = z$1.enum([
	"never_allow",
	"ask_every_time",
	"automatically_allow"
]);
const agentProfileCreationRequestSourceSchema = z$1.enum([
	"web",
	"cli",
	"local_agent",
	"center_agent"
]);
const agentProfileCreationSessionStrategySchema = z$1.enum([
	"new",
	"resume",
	"fork"
]);
const agentProfileCreationRequestStatusSchema = z$1.enum([
	"pending",
	"approved",
	"declined",
	"cancelled",
	"completed",
	"failed"
]);
const agentProfileCreationRequestStatusReasonSchema = z$1.enum([
	"approval_required",
	"automatically_allowed",
	"self_request",
	"target_policy_denied",
	"target_approved",
	"target_declined",
	"requester_cancelled",
	"profile_created",
	"profile_creation_failed"
]);
const completeAgentProfileCreationConfigurationSchema = agentProfileCreationPreferenceSelectionSchema.safeExtend({
	agentControllerRef: agentControllerRefSchema.nullable(),
	authorizeForOrganization: z$1.boolean().optional().default(true),
	localAgentConnectionCandidateId: identifierSchema.nullable().optional(),
	targetComputerId: identifierSchema.nullable()
}).superRefine((configuration, context) => {
	const hasController = configuration.agentControllerRef !== null;
	const hasComputer = configuration.targetComputerId !== null;
	const hasCandidate = configuration.localAgentConnectionCandidateId != null;
	if (configuration.agentControllerRef === "hosted:remote-agent") {
		for (const key of [
			"targetComputerId",
			"localAgentConnectionCandidateId",
			"defaultProjectId",
			"agentControllerCollaborationMode",
			"agentControllerPermissionMode",
			"openCodePermission",
			"reasoningLevel"
		]) if (configuration[key] != null) context.addIssue({
			code: "custom",
			message: "Remote Agents do not use local agent settings.",
			path: [key]
		});
		return;
	}
	if (hasController !== hasComputer) context.addIssue({
		code: "custom",
		message: "agentControllerRef and targetComputerId must be provided together.",
		path: hasController ? ["targetComputerId"] : ["agentControllerRef"]
	});
	if (hasCandidate && !(hasController && hasComputer)) context.addIssue({
		code: "custom",
		message: "localAgentConnectionCandidateId requires an Agent Controller and Computer.",
		path: ["localAgentConnectionCandidateId"]
	});
	if (configuration.reasoningLevel !== null && configuration.modelId === null) context.addIssue({
		code: "custom",
		message: "reasoningLevel requires modelId.",
		path: ["reasoningLevel"]
	});
	if (!hasController) {
		if ([
			configuration.agentControllerCollaborationMode,
			configuration.agentControllerPermissionMode,
			configuration.modelId,
			configuration.openCodePermission,
			configuration.reasoningLevel
		].find((value) => value !== null) !== void 0) context.addIssue({
			code: "custom",
			message: "Local agent settings require an Agent Controller.",
			path: ["agentControllerRef"]
		});
	}
});
const agentProfileCreationConfigurationSchema = z$1.preprocess(addMissingAgentControllerCollaborationMode, completeAgentProfileCreationConfigurationSchema);
function addMissingAgentControllerCollaborationMode(value) {
	if (typeof value !== "object" || value === null || Array.isArray(value)) return value;
	return {
		...value,
		...Object.hasOwn(value, "agentControllerCollaborationMode") ? {} : { agentControllerCollaborationMode: null }
	};
}
const DEFAULT_AGENT_PROFILE_CREATION_CONFIGURATION = {
	agentControllerCollaborationMode: null,
	agentControllerPermissionMode: null,
	agentControllerRef: null,
	authorizeForOrganization: true,
	defaultProjectId: null,
	modelId: null,
	openCodePermission: null,
	reasoningLevel: null,
	targetComputerId: null
};
const agentProfileCreationRequestSchema = z$1.strictObject({
	actorProfileId: identifierSchema,
	completedAt: isoDateTimeSchema.nullable(),
	configuration: agentProfileCreationConfigurationSchema,
	createdAgentProfileId: identifierSchema.nullable(),
	createdAt: isoDateTimeSchema,
	decidedAt: isoDateTimeSchema.nullable(),
	id: identifierSchema,
	failureMessage: z$1.string().trim().min(1).max(1e3).nullable(),
	nativeSessionId: identifierSchema.nullable(),
	organizationId: identifierSchema,
	profileName: profileNameSchema,
	requestedSpaceId: identifierSchema.nullable(),
	requesterUserId: identifierSchema,
	sessionStrategy: agentProfileCreationSessionStrategySchema,
	source: agentProfileCreationRequestSourceSchema,
	status: agentProfileCreationRequestStatusSchema,
	statusReason: agentProfileCreationRequestStatusReasonSchema,
	targetOwnerUserId: identifierSchema,
	updatedAt: isoDateTimeSchema
}).superRefine((request, context) => {
	if (request.sessionStrategy === "new" && request.nativeSessionId !== null) context.addIssue({
		code: "custom",
		message: "New-session requests cannot include nativeSessionId.",
		path: ["nativeSessionId"]
	});
	if (request.sessionStrategy !== "new" && request.nativeSessionId === null) context.addIssue({
		code: "custom",
		message: "Resume and fork requests require nativeSessionId.",
		path: ["nativeSessionId"]
	});
	if (request.status === "completed" && request.createdAgentProfileId === null) context.addIssue({
		code: "custom",
		message: "Completed requests require a created Agent Profile.",
		path: ["createdAgentProfileId"]
	});
	if (request.status === "completed" !== (request.completedAt !== null)) context.addIssue({
		code: "custom",
		message: "Only completed requests can have a completion time.",
		path: ["status"]
	});
	if (request.status === "failed" !== (request.failureMessage !== null)) context.addIssue({
		code: "custom",
		message: "Failed requests require a failure message.",
		path: ["failureMessage"]
	});
});
const createAgentProfileCreationRequestBodySchema = z$1.strictObject({
	configuration: agentProfileCreationConfigurationSchema.optional(),
	idempotencyKey: identifierSchema,
	nativeSessionId: identifierSchema.nullable().optional(),
	profileName: profileNameSchema,
	requestedSpaceId: identifierSchema.nullable().optional(),
	sessionStrategy: agentProfileCreationSessionStrategySchema,
	source: agentProfileCreationRequestSourceSchema,
	targetOwnerUserId: identifierSchema
}).superRefine((request, context) => {
	const nativeSessionId = request.nativeSessionId ?? null;
	if (request.configuration?.agentControllerRef === "hosted:remote-agent" && request.sessionStrategy !== "new") context.addIssue({
		code: "custom",
		message: "Remote Agents start a new conversation.",
		path: ["sessionStrategy"]
	});
	if (request.sessionStrategy === "new" && nativeSessionId !== null) context.addIssue({
		code: "custom",
		message: "New-session requests cannot include nativeSessionId.",
		path: ["nativeSessionId"]
	});
	if (request.sessionStrategy !== "new" && nativeSessionId === null) context.addIssue({
		code: "custom",
		message: "Resume and fork requests require nativeSessionId.",
		path: ["nativeSessionId"]
	});
});
const createAgentProfileCreationRequestResponseSchema = z$1.strictObject({
	replayed: z$1.boolean(),
	request: agentProfileCreationRequestSchema
});
const agentProfileCreationIntentSchema = z$1.object(createAgentProfileCreationRequestBodySchema.shape).transform((intent) => ({
	configuration: agentProfileCreationConfigurationSchema.parse(intent.configuration ?? DEFAULT_AGENT_PROFILE_CREATION_CONFIGURATION),
	nativeSessionId: intent.nativeSessionId ?? null,
	profileName: intent.profileName,
	requestedSpaceId: intent.requestedSpaceId ?? null,
	sessionStrategy: intent.sessionStrategy,
	source: intent.source,
	targetOwnerUserId: intent.targetOwnerUserId
}));
const listAgentProfileCreationRequestsResponseSchema = z$1.strictObject({
	organizationId: identifierSchema,
	matchedIdempotencyKey: identifierSchema.optional(),
	requests: z$1.array(agentProfileCreationRequestSchema).max(200)
});
const decideAgentProfileCreationRequestBodySchema = z$1.strictObject({ decision: z$1.enum(["approve", "decline"]) });
const agentProfileCreationRequestMutationResponseSchema = z$1.strictObject({ request: agentProfileCreationRequestSchema });
const agentProfileCreationApprovalPreferenceSchema = z$1.strictObject({
	existingSessionPolicy: agentProfileCreationApprovalPolicySchema,
	newSessionPolicy: agentProfileCreationApprovalPolicySchema,
	organizationId: identifierSchema,
	updatedAt: isoDateTimeSchema.nullable(),
	userId: identifierSchema
});
const setAgentProfileCreationApprovalPreferenceBodySchema = z$1.strictObject({
	existingSessionPolicy: agentProfileCreationApprovalPolicySchema,
	newSessionPolicy: agentProfileCreationApprovalPolicySchema
});
const getAgentProfileCreationApprovalPreferenceResponseSchema = agentProfileCreationApprovalPreferenceSchema;
const setAgentProfileCreationApprovalPreferenceResponseSchema = agentProfileCreationApprovalPreferenceSchema;
//#endregion
export { DEFAULT_AGENT_PROFILE_CREATION_CONFIGURATION, TEAMMATE_AGENT_REQUESTS_ENABLED, agentProfileCreationApprovalPolicySchema, agentProfileCreationApprovalPreferenceSchema, agentProfileCreationConfigurationSchema, agentProfileCreationExecutionMessageSchema, agentProfileCreationIntentSchema, agentProfileCreationRequestMutationResponseSchema, agentProfileCreationRequestSchema, agentProfileCreationRequestSelectorSchema, agentProfileCreationRequestSourceSchema, agentProfileCreationRequestStatusReasonSchema, agentProfileCreationRequestStatusSchema, agentProfileCreationSessionStrategySchema, createAgentProfileCreationRequestBodySchema, createAgentProfileCreationRequestResponseSchema, decideAgentProfileCreationRequestBodySchema, getAgentProfileCreationApprovalPreferenceResponseSchema, listAgentProfileCreationRequestsResponseSchema, setAgentProfileCreationApprovalPreferenceBodySchema, setAgentProfileCreationApprovalPreferenceResponseSchema };

//# sourceMappingURL=agent-profile-creation-request.js.map